/**
 * HTML Preview Component
 *
 * Renders a live preview of the user's style selections in an iframe.
 * Uses the unified renderer to ensure consistency with prompt generation.
 */

import React, { useMemo } from "react";
import type {
  PageType,
  StyleType,
  ThemeType,
  AccentColor,
  SpacingType,
  TypographyType,
  AnimationType,
  CornerStyleType,
  ShadowType,
} from "@/store/zustand/stylesBuilderStore";
import { cn } from "@/lib/utils";
import {
  renderFromSelections,
  renderForTheme,
} from "@/utils/stylesBuilderRenderer";
import type { StyleSelections } from "@/utils/styleResolver";
import { getComponentsForPageType } from "./componentRegistry";

interface HTMLPreviewProps {
  pageType: PageType | null;
  style: StyleType | null;
  theme: ThemeType | null;
  accentColor: AccentColor | null;
  typography: TypographyType | null;
  spacing: SpacingType | null;
  animation: AnimationType | null;
  cornerStyle: CornerStyleType | null;
  shadow: ShadowType | null;
  customAccentHex?: string;
  className?: string;
  /** Optional: specify which components to show in preview */
  componentTypes?: string[];
}

export const HTMLPreview: React.FC<HTMLPreviewProps> = (props) => {
  const {
    pageType,
    theme,
    className,
    componentTypes,
    style,
    accentColor,
    typography,
    spacing,
    animation,
    cornerStyle,
    shadow,
    customAccentHex,
  } = props;

  const selections = useMemo(
    (): StyleSelections => ({
      style,
      theme,
      accentColor,
      typography,
      spacing,
      animation,
      cornerStyle,
      shadow,
      customAccentHex,
    }),
    [
      style,
      theme,
      accentColor,
      typography,
      spacing,
      animation,
      cornerStyle,
      shadow,
      customAccentHex,
    ],
  );

  const effectiveComponentTypes = useMemo(() => {
    if (componentTypes && componentTypes.length > 0) {
      return componentTypes;
    }
    return getComponentsForPageType(pageType, style);
  }, [componentTypes, pageType, style]);

  const htmlContent = useMemo(() => {
    if (theme === "both") return "";
    const output = renderFromSelections(selections, effectiveComponentTypes);
    return output.fullHTML;
  }, [theme, selections, effectiveComponentTypes]);

  const lightContent = useMemo(() => {
    if (theme !== "both") return "";
    const output = renderForTheme(selections, "light", effectiveComponentTypes);
    return output.fullHTML;
  }, [theme, selections, effectiveComponentTypes]);

  const darkContent = useMemo(() => {
    if (theme !== "both") return "";
    const output = renderForTheme(selections, "dark", effectiveComponentTypes);
    return output.fullHTML;
  }, [theme, selections, effectiveComponentTypes]);

  if (theme === "both") {
    return (
      <div className={cn("flex gap-4 h-full p-2", className)}>
        <div className="flex-1 flex flex-col min-w-0">
          <span className="text-xs font-medium text-text-muted mb-2 uppercase tracking-wider">
            Light
          </span>
          <div className="flex-1 overflow-auto rounded-lg border border-secondary-n-800">
            <iframe
              srcDoc={lightContent}
              className="w-full h-full border-0"
              title="Light Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          <span className="text-xs font-medium text-text-muted mb-2 uppercase tracking-wider">
            Dark
          </span>
          <div className="flex-1 overflow-auto rounded-lg border border-secondary-n-800">
            <iframe
              srcDoc={darkContent}
              className="w-full h-full border-0"
              title="Dark Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("overflow-auto", className)}>
      <iframe
        srcDoc={htmlContent}
        className="w-full h-full border-0"
        title="Live Preview"
        sandbox="allow-same-origin"
      />
    </div>
  );
};

export default HTMLPreview;
